import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon, PrinterIcon } from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import toast from "react-hot-toast";
import { reportsApi } from "../../api/reports";
import { rankingsApi } from "../../api/rankings";
import { campersApi } from "../../api/campers";
import { useSeason } from "../../context/SeasonContext";
import { formatCabinNumber } from "../../utils/terminology";
import type { CamperKerchiefData, KerchiefActivity, Camper, CamperRankEntry } from "../../types";
import { computeClockwiseSlots } from "../../utils/kerchiefSlots";

// ─── Thumbnail downscaling ────────────────────────────────────────────────────
// Browser-printed PDFs embed <img> sources at their full original resolution
// regardless of the CSS display size, which is why this report's PDFs were
// ~13MB per camper (each badge thumbnail was a full-size source image). We
// downscale every patch image to a small JPEG via canvas and use that data URL
// as the src — typical badge contributes a few KB to the PDF instead of
// hundreds. Keyed cache means each unique thumbnail URL is fetched and
// downscaled exactly once across the whole session.

const PATCH_PRINT_PX = 96;     // ~3x the on-page patch (28px) for crisp print
const PATCH_JPEG_Q   = 0.78;

const thumbCache = new Map<string, Promise<string>>();

function downscaleThumbnail(url: string): Promise<string> {
  const cached = thumbCache.get(url);
  if (cached) return cached;
  const p = new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = PATCH_PRINT_PX;
        canvas.height = PATCH_PRINT_PX;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("canvas 2d unavailable");
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        // cover-fit so the circular crop never reveals empty corners
        const scale = Math.max(PATCH_PRINT_PX / w, PATCH_PRINT_PX / h);
        const dw = w * scale;
        const dh = h * scale;
        ctx.drawImage(img, (PATCH_PRINT_PX - dw) / 2, (PATCH_PRINT_PX - dh) / 2, dw, dh);
        resolve(canvas.toDataURL("image/jpeg", PATCH_JPEG_Q));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error(`thumbnail load failed: ${url}`));
    img.src = url;
  });
  // On failure (e.g., CORS-tainted canvas), drop the cache entry so a later
  // attempt can retry. The hook falls back to the original URL.
  p.catch(() => thumbCache.delete(url));
  thumbCache.set(url, p);
  return p;
}

function useDownscaledThumbnail(url: string | null | undefined): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!url) { setSrc(null); return; }
    let cancelled = false;
    downscaleThumbnail(url)
      .then((d) => { if (!cancelled) setSrc(d); })
      .catch(() => { if (!cancelled) setSrc(url); }); // fall back to raw URL
    return () => { cancelled = true; };
  }, [url]);
  return src;
}

async function preloadThumbnails(campers: CamperKerchiefData[]): Promise<void> {
  const urls = new Set<string>();
  for (const c of campers) {
    for (const a of c.activities) {
      if (a.activity.thumbnailUrl) urls.add(a.activity.thumbnailUrl);
    }
  }
  await Promise.all([...urls].map((u) => downscaleThumbnail(u).catch(() => null)));
}

// ─── Print CSS ────────────────────────────────────────────────────────────────

const PRINT_STYLE = `
@media print {
  @page { size: letter portrait; margin: 0.5in; }
  .no-print { display: none !important; }
  .camper-page { break-after: page; page-break-after: always; }
  .camper-page:last-child { break-after: auto; page-break-after: auto; }
  body { background: white !important; }
  .print-container { padding: 0 !important; }
}
`;

// ─── Kerchief slot layout ─────────────────────────────────────────────────────
//
// Same 26 clockwise print slots as KerchiefView and the Kerchief Layout
// editor (utils/kerchiefSlots.ts), with tighter margins for this printout.

const TOP_Y_PCT  = 15;   // Top row sits close to the hem; info tables stack
                         // above the patches in the gap, then again below the
                         // diagonal/apex patches.
// Slot range pulled in from 10..90 → 16..84 so the corner top-row patches
// (and the cards stacked below the first/last interior side slots and the
// apex) clear the triangle's diagonals. The triangle's left edge is x=y;
// each patch/card needs slot.x − card_half_width ≥ patch_bottom_y + cardH
// at its bottom-left corner, which constrains how close to the diagonal a
// slot can sit.
const SLOTS = computeClockwiseSlots({
  topY: TOP_Y_PCT,
  xMin: 16,
  xMax: 84,
  apexLeft: 50,
  apexTop: 82,
});

// ─── Printable kerchief (white background, red outline) ──────────────────────

const KERCHIEF_W = 720;
const KERCHIEF_H = 360;
const PATCH_SIZE = 28;
// Card widths. Edge slots sit ~31px apart along each diagonal, so an edge
// card must stay under ~33px or it clips the next patch down. The top row
// has ~49px pitch, so its cards get extra room for longer names.
const INFO_W_TOP  = 44;
const INFO_W_EDGE = 32;

function formatYearShort(y: number): string { return `'${String(y).slice(2)}`; }

function Patch({ activity }: { activity: KerchiefActivity["activity"] }) {
  const src = useDownscaledThumbnail(activity.thumbnailUrl);
  return (
    <div
      style={{
        width: PATCH_SIZE,
        height: PATCH_SIZE,
        borderRadius: "50%",
        overflow: "hidden",
        border: "1px solid #555",
        background: "#f5f5f4",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={activity.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : null}
    </div>
  );
}

// Activity name + tally stacked next to each patch (above the top row,
// below the diagonals and apex). No frame — the kerchief itself is the
// frame.
function InfoBlock({
  a,
  position,
}: { a: KerchiefActivity; position: "above" | "below" }) {
  const tallyParts: string[] = [];
  if (a.completedAcs > 0) tallyParts.push(String(a.completedAcs));
  if (a.honors.length > 0) tallyParts.push(`${a.honors.length}H`);
  if (a.graduated)        tallyParts.push("★");

  const positioning: React.CSSProperties = position === "above"
    ? { bottom: "100%", marginBottom: 2 }
    : { top: "100%", marginTop: 2 };

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        width: position === "above" ? INFO_W_TOP : INFO_W_EDGE,
        textAlign: "center",
        color: "#000",
        fontFamily: "Georgia, serif",
        ...positioning,
      }}
    >
      <div
        title={a.activity.name}
        style={{
          fontSize: 7,
          fontWeight: 700,
          lineHeight: 1.05,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          // Wrap only at word boundaries (e.g., "Disc Golf"), never split
          // a single word like "Basketball" mid-character.
          overflowWrap: "normal",
          wordBreak: "normal",
        }}
      >
        {a.activity.name}
      </div>
      {/* Always render the tally row — even when empty — so the activity
          name on every top-row card sits at a consistent height above the
          patch instead of riding lower for badges with no ACs/honors. */}
      <div
        style={{
          fontSize: 7,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontWeight: 700,
          lineHeight: 1.05,
          marginTop: 1,
          visibility: tallyParts.length > 0 ? "visible" : "hidden",
        }}
      >
        {tallyParts.length > 0 ? tallyParts.join(" · ") : "0"}
      </div>
    </div>
  );
}

function PrintableKerchief({ data, ranks }: { data: CamperKerchiefData; ranks: CamperRankEntry[] | null }) {
  return (
    <div style={{ width: KERCHIEF_W, margin: "0 auto" }}>
      {/* Hem with name + years */}
      <div
        style={{
          border: "1.5px solid #b71c1c",
          borderBottom: "none",
          padding: "4px 14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "Georgia, serif",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: "#000", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {data.camper.firstName} {data.camper.lastName}
        </span>
        <span style={{ fontSize: 11, fontFamily: "monospace", color: "#000" }}>
          {data.yearsAttended.length === 0
            ? <em style={{ color: "#666" }}>No years recorded</em>
            : data.yearsAttended.map(formatYearShort).join("  ·  ")}
        </span>
      </div>

      {/* Triangle */}
      <div style={{ position: "relative", width: KERCHIEF_W, height: KERCHIEF_H }}>
        <svg
          width={KERCHIEF_W}
          height={KERCHIEF_H}
          viewBox={`0 0 ${KERCHIEF_W} ${KERCHIEF_H}`}
          style={{ position: "absolute", inset: 0 }}
        >
          <polygon
            points={`0,0 ${KERCHIEF_W},0 ${KERCHIEF_W / 2},${KERCHIEF_H}`}
            fill="white"
            stroke="#b71c1c"
            strokeWidth="1.5"
          />
        </svg>

        {/* Badges. Wrapper is sized to the patch so percentage positioning
            centers the patch on the slot point; one mini-table per badge
            sits above (top row) or below (sides + apex) the patch. */}
        {data.activities.map((a) => {
          const slot = a.slot != null ? SLOTS[a.slot] : undefined;
          if (!slot) return null;
          // Top row: card above the patch. Edges and tip: card below.
          const blockPos: "above" | "below" = slot.top === TOP_Y_PCT ? "above" : "below";
          return (
            <div
              key={a.activity.id}
              style={{
                position: "absolute",
                left: `${slot.left}%`,
                top:  `${slot.top}%`,
                width: PATCH_SIZE,
                height: PATCH_SIZE,
                transform: "translate(-50%, -50%)",
              }}
            >
              <Patch activity={a.activity} />
              <InfoBlock a={a} position={blockPos} />
            </div>
          );
        })}

        {/* Summary blocks tucked into the lower-left and lower-right white space
            outside the triangle's diagonals. */}
        <SummaryOverlay data={data} ranks={ranks} />
      </div>
    </div>
  );
}

// ─── Year/Total summary computations ─────────────────────────────────────────

interface YearTotals { year: number; acs: number; honors: number; grads: number; cabins: string[] }

function computeYearTotals(data: CamperKerchiefData): { rows: YearTotals[]; total: YearTotals } {
  const map = new Map<number, YearTotals>();
  const ensure = (y: number) => {
    let r = map.get(y);
    if (!r) {
      r = {
        year: y,
        acs: 0,
        honors: 0,
        grads: 0,
        cabins: data.cabinsByYear?.[y] ?? [],
      };
      map.set(y, r);
    }
    return r;
  };
  for (const y of data.yearsAttended) ensure(y);
  for (const a of data.activities) {
    for (const y of a.completionYears) ensure(y).acs += 1;
    for (const h of a.honors) {
      if (h.year != null) ensure(h.year).honors += 1;
    }
    if (a.graduated && a.graduationDate) {
      const y = Number(a.graduationDate.slice(0, 4));
      if (!Number.isNaN(y)) ensure(y).grads += 1;
    }
  }
  const rows = [...map.values()].sort((a, b) => a.year - b.year);
  const total = rows.reduce(
    (acc, r) => ({ year: 0, acs: acc.acs + r.acs, honors: acc.honors + r.honors, grads: acc.grads + r.grads, cabins: [] }),
    { year: 0, acs: 0, honors: 0, grads: 0, cabins: [] as string[] },
  );
  return { rows, total };
}

const RANK_METRIC_LABEL: Record<string, string> = {
  acs: "ACs",
  honors: "Honors",
  graduations: "Grads",
};

const RANK_SCOPE_LABEL: Record<string, string> = {
  "all-time": "All",
  "current-term": "Term",
  active: "Active",
};

const RANK_METRIC_ORDER = ["acs", "honors", "graduations"] as const;
const RANK_SCOPE_ORDER = ["all-time", "active", "current-term"] as const;

// Two info blocks tucked into the triangle's left/right white space:
// left = lifetime totals + camper rankings stacked vertically; right =
// per-year breakdown.
function SummaryOverlay({ data, ranks }: { data: CamperKerchiefData; ranks: CamperRankEntry[] | null }) {
  const { rows, total } = computeYearTotals(data);

  // Determine which scopes are populated (depends on whether a term was
  // selected and whether the camper has matching enrollments).
  const visibleScopes = RANK_SCOPE_ORDER.filter((scope) =>
    ranks?.some((r) => r.scope === scope && r.rank != null)
  );
  const hasRanks = visibleScopes.length > 0;

  return (
    <>
      {/* Left: lifetime totals (top) stacked above rankings (bottom). Both
          anchored to the lower-left wedge outside the triangle's diagonal. */}
      <div
        style={{
          position: "absolute",
          left: 4,
          bottom: 4,
          width: 150,
          fontFamily: "Georgia, serif",
          fontSize: 9,
          color: "#000",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 10, borderBottom: "0.5px solid #57534e", paddingBottom: 1, marginBottom: 3 }}>
            Totals
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 2 }}>
            <span>ACs</span>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{total.acs}</span>
            <span>Honors</span>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{total.honors}</span>
            <span>Graduations</span>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{total.grads}</span>
          </div>
        </div>

        {hasRanks && ranks && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 10, borderBottom: "0.5px solid #57534e", paddingBottom: 1, marginBottom: 2 }}>
              Rankings
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 8,
                lineHeight: 1.05,
                fontVariantNumeric: "tabular-nums",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ color: "#57534e" }}>
                  <th style={{ textAlign: "left", fontWeight: 700, padding: 0 }}>&nbsp;</th>
                  {visibleScopes.map((s) => (
                    <th key={s} style={{ textAlign: "right", fontWeight: 700, padding: "0 0 0 4px" }}>
                      {RANK_SCOPE_LABEL[s]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RANK_METRIC_ORDER.map((metric) => {
                  const entriesForMetric = ranks.filter((r) => r.metric === metric);
                  if (entriesForMetric.every((r) => r.rank == null)) return null;
                  return (
                    <tr key={metric}>
                      <td style={{ padding: 0, fontWeight: 600 }}>{RANK_METRIC_LABEL[metric]}</td>
                      {visibleScopes.map((scope) => {
                        const entry = entriesForMetric.find((r) => r.scope === scope);
                        const cell =
                          entry && entry.rank != null
                            ? `${entry.rank}/${entry.total}`
                            : "—";
                        return (
                          <td
                            key={scope}
                            style={{
                              textAlign: "right",
                              padding: "0 0 0 4px",
                              color: entry && entry.rank != null ? "#000" : "#a8a29e",
                            }}
                          >
                            {cell}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Right: per-year summary stacked vertically */}
      <div
        style={{
          position: "absolute",
          right: 4,
          bottom: 4,
          width: 170,
          fontFamily: "Georgia, serif",
          fontSize: 8,
          color: "#000",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 10, borderBottom: "0.5px solid #57534e", paddingBottom: 1, marginBottom: 2 }}>
          By Year
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 8,
            lineHeight: 1.05,
            fontVariantNumeric: "tabular-nums",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: 26 }} />
            <col />
            <col style={{ width: 22 }} />
            <col style={{ width: 16 }} />
            <col style={{ width: 16 }} />
          </colgroup>
          <thead>
            <tr style={{ color: "#57534e" }}>
              <th style={{ textAlign: "left",  fontWeight: 700, padding: 0 }}>Year</th>
              <th style={{ textAlign: "left",  fontWeight: 700, padding: "0 0 0 4px" }}>Cabin</th>
              <th style={{ textAlign: "right", fontWeight: 700, padding: "0 4px 0 0" }}>ACs</th>
              <th style={{ textAlign: "right", fontWeight: 700, padding: "0 4px 0 0" }}>H</th>
              <th style={{ textAlign: "right", fontWeight: 700, padding: 0           }}>★</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ fontStyle: "italic", color: "#78716c" }}>—</td>
              </tr>
            ) : (
              rows.map((r) => {
                const cabinText = r.cabins.length > 0
                  ? r.cabins.map((c) => formatCabinNumber(c)).join(", ")
                  : "";
                return (
                  <tr key={r.year}>
                    <td style={{ padding: 0 }}>{r.year}</td>
                    <td
                      style={{
                        padding: "0 0 0 4px",
                        fontVariantNumeric: "normal",
                        color: cabinText ? "#000" : "#a8a29e",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={cabinText}
                    >
                      {cabinText || "—"}
                    </td>
                    <td style={{ textAlign: "right", padding: "0 4px 0 0" }}>{r.acs}</td>
                    <td style={{ textAlign: "right", padding: "0 4px 0 0", color: r.honors ? "#000" : "#a8a29e" }}>{r.honors}</td>
                    <td style={{ textAlign: "right", padding: 0, color: r.grads ? "#000" : "#a8a29e" }}>{r.grads}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Activity matrix (single table: activities × AC display number) ──────────

function yy(date: string | null, fallbackYear: number | null = null): string {
  if (date && date.length >= 4) return date.slice(2, 4);
  if (fallbackYear != null) return String(fallbackYear).slice(2);
  return "";
}

function ActivityMatrix({ data }: { data: CamperKerchiefData }) {
  const maxAcs = Math.max(0, ...data.activities.map((a) => a.acs.length));
  const acCols = Array.from({ length: maxAcs }, (_, i) => i + 1);

  const sorted = [...data.activities].sort((a, b) =>
    a.activity.name.localeCompare(b.activity.name),
  );

  const matrixCellTH: React.CSSProperties = {
    border: "0.5px solid #a8a29e",
    padding: "2px 3px",
    fontWeight: 700,
    fontSize: 8,
    textAlign: "center",
    background: "#f5f5f4",
  };
  const matrixCellTD: React.CSSProperties = {
    border: "0.5px solid #d6d3d1",
    padding: "1.5px 3px",
    fontSize: 8,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 8,
        marginTop: 8,
        tableLayout: "fixed",
      }}
    >
      <colgroup>
        <col style={{ width: 110 }} />
        {acCols.map((n) => <col key={n} />)}
        <col />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th style={{ ...matrixCellTH, textAlign: "left" }}>Activity</th>
          {acCols.map((n) => (
            <th key={n} style={matrixCellTH}>{n}</th>
          ))}
          <th style={matrixCellTH} title="Honors earned">H</th>
          <th style={matrixCellTH} title="Graduated">★</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((a) => {
          const sortedAcs = [...a.acs].sort((x, y) => x.sortOrder - y.sortOrder);
          const cells: string[] = Array(maxAcs).fill("");
          sortedAcs.forEach((ac, idx) => {
            if (idx >= maxAcs) return;
            if (ac.completed) cells[idx] = yy(ac.completedDate) || "✓";
          });
          const honorCount = a.honors.length;
          const honorText = honorCount > 0 ? String(honorCount) : "";
          const gradText = a.graduated ? (yy(a.graduationDate) || "✓") : "";
          return (
            <tr key={a.activity.id}>
              <td
                style={{ ...matrixCellTD, textAlign: "left", fontWeight: 600, padding: "1.5px 5px" }}
                title={a.activity.archived ? `${a.activity.name} (Archived)` : a.activity.name}
              >
                {a.activity.name}
                {a.activity.archived && (
                  <span style={{ fontStyle: "italic", fontWeight: 400, color: "#888", marginLeft: 4 }}>
                    (Arch.)
                  </span>
                )}
              </td>
              {cells.map((c, i) => (
                <td
                  key={i}
                  style={{ ...matrixCellTD, color: c ? "#000" : "#d6d3d1" }}
                >
                  {c}
                </td>
              ))}
              <td style={{ ...matrixCellTD, fontWeight: honorText ? 700 : 400, color: honorText ? "#000" : "#d6d3d1" }}>
                {honorText || ""}
              </td>
              <td style={{ ...matrixCellTD, fontWeight: gradText ? 700 : 400, color: gradText ? "#000" : "#d6d3d1" }}>
                {gradText || ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Detailed AC list page ───────────────────────────────────────────────────

interface DetailedAcEntry {
  key: string;
  displayLabel: string;
  name: string;
  description: string;
  year: number | null;
  earned: boolean;
  archived: boolean;
}

interface DetailedActivityGroup {
  activityId: number;
  activityName: string;
  activityArchived: boolean;
  entries: DetailedAcEntry[];
}

function buildDetailedGroups(
  data: CamperKerchiefData,
  includeAllAcs: boolean,
): DetailedActivityGroup[] {
  const groups: DetailedActivityGroup[] = [];
  for (const a of data.activities) {
    const acs = includeAllAcs ? a.acs : a.acs.filter((ac) => ac.completed);
    const entries = [...acs]
      .sort((x, y) => x.displayNumber - y.displayNumber)
      .map<DetailedAcEntry>((ac) => ({
        key: `ac-${ac.id}`,
        displayLabel: String(ac.displayNumber),
        name: ac.name,
        description: ac.description,
        year: ac.completed && ac.completedDate ? new Date(ac.completedDate).getFullYear() : null,
        earned: ac.completed,
        archived: ac.archived,
      }));

    const showGraduation = a.graduationAcDetail && (includeAllAcs || a.graduated);
    if (showGraduation && a.graduationAcDetail) {
      entries.push({
        key: `grad-${a.activity.id}`,
        displayLabel: "Grad",
        name: a.graduationAcDetail.name,
        description: a.graduationAcDetail.description,
        year: a.graduated && a.graduationDate ? new Date(a.graduationDate).getFullYear() : null,
        earned: a.graduated,
        archived: a.graduationAcDetail.archived,
      });
    }

    if (entries.length === 0) continue;
    groups.push({
      activityId: a.activity.id,
      activityName: a.activity.name,
      activityArchived: a.activity.archived,
      entries,
    });
  }
  groups.sort((x, y) => x.activityName.localeCompare(y.activityName, undefined, { sensitivity: "base" }));
  return groups;
}

const ARCHIVED_TAG_STYLE = {
  fontFamily: "Georgia, serif",
  fontStyle: "italic",
  fontWeight: 400,
  color: "#888",
  marginLeft: 6,
  letterSpacing: "0.02em",
} as const;

function YearMarker({ year, earned }: { year: number | null; earned: boolean }) {
  // Earned ACs display the year in a filled red box so the achievement pops on
  // the printed page. Unearned ACs render an empty bordered box at the same
  // size, intended as a write-in slot for the camper.
  const base = {
    flex: "0 0 38px",
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10,
    borderRadius: 3,
  } as const;
  if (earned && year != null) {
    return (
      <span
        style={{
          ...base,
          background: "#b71c1c",
          color: "white",
          fontWeight: 700,
          border: "1px solid #b71c1c",
          letterSpacing: "0.05em",
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
        }}
      >
        {formatYearShort(year)}
      </span>
    );
  }
  return (
    <span
      style={{
        ...base,
        background: "white",
        color: "#333",
        border: "1px dashed #999",
      }}
      aria-label="Write in year if earned"
    >
      {/* empty — campers fill this in by hand */}
      &nbsp;
    </span>
  );
}

function DetailedAcPage({
  data,
  includeAllAcs,
}: {
  data: CamperKerchiefData;
  includeAllAcs: boolean;
}) {
  const groups = buildDetailedGroups(data, includeAllAcs);
  const title = includeAllAcs ? "Achievement Credit Tracker" : "Achievement Credits Earned";
  return (
    <section className="camper-page" style={{ padding: 0 }}>
      <div style={{ width: 720, margin: "0 auto", padding: "8px 14px 24px", fontFamily: "Georgia, serif", color: "#000" }}>
        <div
          style={{
            borderBottom: "1.5px solid #b71c1c",
            paddingBottom: 6,
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {data.camper.firstName} {data.camper.lastName} — {title}
          </span>
          <span style={{ fontSize: 11, fontFamily: "monospace" }}>
            {data.yearsAttended.length === 0
              ? <em style={{ color: "#666" }}>No years recorded</em>
              : data.yearsAttended.map(formatYearShort).join("  ·  ")}
          </span>
        </div>

        {includeAllAcs && (
          <p style={{ fontSize: 10, color: "#555", marginTop: 0, marginBottom: 10, fontStyle: "italic" }}>
            Earned credits show the year. Blank boxes are for credits not yet earned —
            write in the year when you complete one.
          </p>
        )}

        {groups.length === 0 ? (
          <p style={{ fontSize: 11, color: "#666" }}>
            {includeAllAcs ? "No achievement credits available." : "No achievement credits earned."}
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.activityId} style={{ marginBottom: 10, breakInside: "avoid", pageBreakInside: "avoid" }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  borderBottom: "0.75px solid #999",
                  paddingBottom: 2,
                  marginBottom: 4,
                }}
              >
                {g.activityName}
                {g.activityArchived && (
                  <span style={{ ...ARCHIVED_TAG_STYLE, textTransform: "none", fontSize: 10 }}>
                    (Archived)
                  </span>
                )}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {g.entries.map((e) => (
                  <li
                    key={e.key}
                    style={{
                      display: "flex",
                      gap: 8,
                      padding: "3px 0",
                      fontSize: 10.5,
                      lineHeight: 1.35,
                      alignItems: "flex-start",
                      breakInside: "avoid",
                      pageBreakInside: "avoid",
                    }}
                  >
                    <span
                      style={{
                        flex: "0 0 28px",
                        textAlign: "right",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontWeight: 700,
                        paddingTop: 2,
                      }}
                    >
                      {e.displayLabel}.
                    </span>
                    {includeAllAcs ? (
                      <YearMarker year={e.year} earned={e.earned} />
                    ) : (
                      e.year != null && (
                        <span
                          style={{
                            flex: "0 0 38px",
                            paddingTop: 2,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            color: "#555",
                          }}
                        >
                          {formatYearShort(e.year)}
                        </span>
                      )
                    )}
                    <span style={{ flex: 1, paddingTop: 1 }}>
                      <span style={{ fontWeight: 700 }}>{e.name}</span>
                      {e.archived && (
                        <span style={ARCHIVED_TAG_STYLE}>(Archived)</span>
                      )}
                      {e.description && (
                        <span style={{ color: "#222" }}>
                          {" — "}
                          {e.description}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ─── Single camper page ───────────────────────────────────────────────────────

function CamperPage({
  data,
  ranks,
  detailedView,
  includeAllAcs,
}: {
  data: CamperKerchiefData;
  ranks: CamperRankEntry[] | null;
  detailedView: boolean;
  includeAllAcs: boolean;
}) {
  return (
    <>
      <section className="camper-page" style={{ padding: 0 }}>
        <PrintableKerchief data={data} ranks={ranks} />
        <ActivityMatrix data={data} />
      </section>
      {detailedView && <DetailedAcPage data={data} includeAllAcs={includeAllAcs} />}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CamperSummaryReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedTermId, selectedYear, selectedTerm } = useSeason();

  const initialId = (() => {
    const id = searchParams.get("camperId");
    return id ? Number(id) : null;
  })();
  const [camperId, setCamperId] = useState<number | null>(initialId);
  const [printAll, setPrintAll] = useState(false);
  const [detailedView, setDetailedView] = useState(searchParams.get("detailed") === "1");
  const [includeAllAcs, setIncludeAllAcs] = useState(searchParams.get("allAcs") === "1");

  function toggleDetailedView(next: boolean) {
    setDetailedView(next);
    const params = new URLSearchParams(searchParams);
    if (next) params.set("detailed", "1");
    else {
      params.delete("detailed");
      params.delete("allAcs");
      setIncludeAllAcs(false);
    }
    setSearchParams(params, { replace: true });
  }

  function toggleIncludeAllAcs(next: boolean) {
    setIncludeAllAcs(next);
    const params = new URLSearchParams(searchParams);
    if (next) params.set("allAcs", "1");
    else params.delete("allAcs");
    setSearchParams(params, { replace: true });
  }

  const [search, setSearch] = useState("");
  const [campers, setCampers] = useState<Camper[]>([]);
  const [listLoading, setListLoading] = useState(true);

  const [singleData, setSingleData] = useState<CamperKerchiefData | null>(null);
  const [singleRanks, setSingleRanks] = useState<CamperRankEntry[] | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);

  const [allData, setAllData] = useState<CamperKerchiefData[]>([]);
  const [allRanks, setAllRanks] = useState<Map<number, CamperRankEntry[]>>(new Map());
  const [allLoading, setAllLoading] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);

  // Inject print CSS
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = PRINT_STYLE;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Load current-term campers for select list
  useEffect(() => {
    setListLoading(true);
    setPrintAll(false);
    const params: { pageSize: number; termId?: number; year?: number; attendance?: "session-only" } = { pageSize: 1000 };
    if (selectedTermId !== null) {
      params.termId = selectedTermId;
      params.attendance = "session-only";
    } else {
      params.year = selectedYear;
    }
    campersApi
      .list(params)
      .then((res) => setCampers(res.data))
      .catch(() => toast.error("Failed to load camper list"))
      .finally(() => setListLoading(false));
  }, [selectedTermId, selectedYear]);

  // Scroll selected camper into view
  useEffect(() => {
    if (!camperId || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-camper-id="${camperId}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [camperId, campers]);

  // Load single camper kerchief data + ranks
  useEffect(() => {
    if (camperId === null || printAll) { setSingleData(null); setSingleRanks(null); return; }
    setSingleLoading(true);
    setSingleRanks(null);
    Promise.all([
      reportsApi.camperKerchief(camperId),
      rankingsApi.camperRanks(camperId, selectedTermId).catch(() => null),
    ])
      .then(([kerchief, ranksRes]) => {
        setSingleData(kerchief);
        setSingleRanks(ranksRes?.ranks ?? null);
      })
      .catch(() => toast.error("Failed to load camper data"))
      .finally(() => setSingleLoading(false));
  }, [camperId, printAll, selectedTermId]);

  const filteredCampers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...campers].sort((a, b) =>
      a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)
    );
    if (!q) return sorted;
    return sorted.filter((c) => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q));
  }, [campers, search]);

  function handleSelect(id: number) {
    setCamperId(id);
    setPrintAll(false);
    const params = new URLSearchParams(searchParams);
    params.set("camperId", String(id));
    setSearchParams(params, { replace: true });
  }

  async function loadAllAndPrint() {
    if (campers.length === 0) {
      toast.error("No campers in current term");
      return;
    }
    setAllLoading(true);
    setPrintAll(true);
    try {
      // Sequential to be gentle on the API; could be parallelized in chunks if needed.
      const results: CamperKerchiefData[] = [];
      const ranksByCamper = new Map<number, CamperRankEntry[]>();
      for (const c of campers) {
        try {
          const [d, r] = await Promise.all([
            reportsApi.camperKerchief(c.id),
            rankingsApi.camperRanks(c.id, selectedTermId).catch(() => null),
          ]);
          results.push(d);
          if (r) ranksByCamper.set(c.id, r.ranks);
        } catch {
          // skip on error
        }
      }
      results.sort((a, b) =>
        a.camper.lastName.localeCompare(b.camper.lastName) ||
        a.camper.firstName.localeCompare(b.camper.firstName)
      );
      setAllData(results);
      setAllRanks(ranksByCamper);
      // Downscale every unique thumbnail to a small JPEG before printing,
      // otherwise the PDF embeds full-resolution images per badge.
      await preloadThumbnails(results);
      // Two animation frames let React swap each <img src> to its data URL
      // before the print dialog snapshots the page.
      requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    } finally {
      setAllLoading(false);
    }
  }

  async function printSingle() {
    if (!singleData) return;
    await preloadThumbnails([singleData]);
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  }

  const termLabel = selectedTerm ? `${selectedTerm.year} ${selectedTerm.name}` : `${selectedYear}`;

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 64px)" }}>
      <div className="no-print">
        <PageHeader title={`Camper Summary${selectedTerm ? ` — ${termLabel}` : ""}`} />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Camper list sidebar */}
        <aside className="no-print flex-shrink-0 w-72 border-r border-stone-200 bg-white flex flex-col" style={{ maxHeight: "calc(100vh - 64px)" }}>
          <div className="p-3 border-b border-stone-200 space-y-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter campers…"
                className="block w-full pl-8 pr-3 py-2 rounded-md border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={loadAllAndPrint}
              disabled={allLoading || campers.length === 0}
              className="w-full flex items-center justify-center gap-1.5 rounded-md bg-forest-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-forest-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <PrinterIcon className="w-4 h-4" />
              {allLoading
                ? `Loading… (${allData.length}/${campers.length})`
                : `Print All (${campers.length})`}
            </button>
            <label className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer">
              <input
                type="checkbox"
                checked={detailedView}
                onChange={(e) => toggleDetailedView(e.target.checked)}
                className="h-3.5 w-3.5 accent-forest-600"
              />
              Detailed view (AC names &amp; descriptions)
            </label>
            <label
              className={`flex items-center gap-1.5 text-xs pl-5 cursor-pointer ${
                detailedView ? "text-stone-700" : "text-stone-400 cursor-not-allowed"
              }`}
            >
              <input
                type="checkbox"
                checked={includeAllAcs}
                disabled={!detailedView}
                onChange={(e) => toggleIncludeAllAcs(e.target.checked)}
                className="h-3.5 w-3.5 accent-forest-600"
              />
              Include All ACs (with write-in spots)
            </label>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto">
            {listLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredCampers.length === 0 ? (
              <p className="text-center text-stone-400 text-sm py-8 px-3">
                {search ? "No campers match." : "No campers in this term."}
              </p>
            ) : (
              filteredCampers.map((c) => (
                <button
                  key={c.id}
                  data-camper-id={c.id}
                  type="button"
                  onClick={() => handleSelect(c.id)}
                  className={`w-full text-left px-4 py-2 text-sm border-b border-stone-100 transition-colors ${
                    camperId === c.id && !printAll
                      ? "bg-forest-50 text-forest-800 font-medium"
                      : "text-stone-700 hover:bg-stone-50"
                  }`}
                >
                  {c.lastName}, {c.firstName}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto bg-stone-100">
          {/* Print controls (single mode) */}
          {!printAll && singleData && (
            <div className="no-print flex items-center gap-3 p-4 bg-white border-b border-stone-200">
              <button
                type="button"
                onClick={printSingle}
                className="flex items-center gap-1.5 rounded-md bg-forest-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-forest-700"
              >
                <PrinterIcon className="w-4 h-4" />
                Print / Save as PDF
              </button>
              <span className="text-xs text-stone-500">
                {singleData.camper.firstName} {singleData.camper.lastName}
              </span>
            </div>
          )}

          {/* Empty state */}
          {!printAll && !camperId && (
            <div className="no-print flex items-center justify-center h-64 text-stone-400 text-sm">
              Select a camper from the list, or "Print All" to print every {termLabel} camper.
            </div>
          )}

          {!printAll && singleLoading && (
            <div className="no-print flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Print container */}
          <div className="print-container bg-white" style={{ padding: 24 }}>
            {!printAll && singleData && (
              <div style={{ width: 720, margin: "0 auto", background: "white" }}>
                <CamperPage data={singleData} ranks={singleRanks} detailedView={detailedView} includeAllAcs={includeAllAcs} />
              </div>
            )}
            {printAll && (
              <div style={{ width: 720, margin: "0 auto", background: "white" }}>
                {allData.length === 0 ? (
                  <p className="no-print text-center text-stone-500 py-8 text-sm">
                    Loading campers…
                  </p>
                ) : (
                  allData.map((d) => (
                    <CamperPage
                      key={d.camper.id}
                      data={d}
                      ranks={allRanks.get(d.camper.id) ?? null}
                      detailedView={detailedView}
                      includeAllAcs={includeAllAcs}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
